import { FC } from "react"
import { ActionElement } from "./ActionElement"
import { Divider, Grid, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { 
  AccountBalance, 
  AddHomeWork, 
  BarChart, 
  DescriptionOutlined, 
  Download, 
  FindInPage, 
  Payment, 
  PermContactCalendar, 
  Power,
  ReceiptOutlined, 
  TungstenOutlined, 
  Visibility 
} from "@mui/icons-material";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { ActionGroupTitleImage } from "./ActionGroupTitleImage";
import useContractation from "@/components/Contractation/hooks/useContractation";
import { useSettingsContext } from "@/components/Settings/hooks/useSettingsContext";
import useAuth from "@/components/Authentication/hooks/useAuth";

const ActionGroups: FC = () => {
  const theme = useTheme();
  const breakpointDownMd = useMediaQuery(theme.breakpoints.down('md'));

  const { isContractationActive, firstContractationPath } = useContractation();
  const { t } = useTranslation();
  const Settings = useSettingsContext();
  const navigate = useNavigate();
  const { tokenVals } = useAuth();

  const firstContractPath = `common:url.contracts.${Settings.features.displayTypes.filter(type => type !== "multipoint")[0]}.default`;

  return <Grid 
    container
    direction="row"
    spacing={{xs: 2, sm: 4, md: 6}}
    sx={{
      pl: {md: "10vw", lg: "15vw"}, 
      pr: {md: "10vw", lg: "15vw"}
    }}
  >
    {/* I use `gap` instead of `spacing` because it breaks when using 
      breakpoints. Perhaps an issue should be raised to the MUI team. 
      https://github.com/mui/material-ui/issues/32999
    */}
    <Grid container item direction="column" gap={{xs: 1}} sm={12} md={4}>

      <Grid item>
        <Stack
          direction={{ xs: "row", md: "column" }}
          spacing={2}
          alignItems="center"
        >
          <ActionGroupTitleImage 
            component="img"
            sx={{
              height: {
                xs: "40px",
                sm: "60px",
                md: "80px"
              },
              width: "auto"
            }}
            src={'/images/tabGroup/contratos.png'}
            fallbackComponent={
              <DescriptionOutlined 
                color="primary"
                sx={{
                  height: {
                    xs: "40px",
                    sm: "60px",
                    md: "80px"
                  },
                  width: "auto"
                }}
              />
            }
          />
          <Typography variant="h4">
            {t("common:text.contracts_title")}
          </Typography>
        </Stack>
      </Grid>

      {isContractationActive && <Grid item>
        <ActionElement
          title={t("common:text.home_contract_create_title")}
          description={t("common:text.home_contract_create_desc")}
          icon={<AddHomeWork />}
          onClick={() => {firstContractationPath && navigate(t(firstContractationPath))}}
        />
      </Grid>
      }

      <Grid item>
        <ActionElement 
          title={t("common:text.home_contract_list_title")}
          description={t("common:text.home_contract_list_desc")}
          icon={<FindInPage />}
          onClick={() => navigate(t(firstContractPath))}
        />
      </Grid>

      <Grid item>
        <ActionElement 
          title={t("common:text.home_contract_download_title")}
          description={t("common:text.home_contract_download_desc")}
          icon={<Download />}
          onClick={() => navigate(t(firstContractPath) + '?action=download')}
        />
      </Grid>

      {Settings.features.powerChange && (
        <Grid item>
          <ActionElement 
            toolTipProps={{
              title: tokenVals?.readonly ?
                t("common:text.token_readonly_tooltip")
              :
                "",
              placement: breakpointDownMd ? "bottom" : "right"
            }}
            title={t("common:text.home_contract_power_change_title")}
            description={t("common:text.home_contract_power_change_desc")}
            icon={<Power />}
            disabled={!!tokenVals?.readonly}
            showTooltip={!!tokenVals?.readonly}
            onClick={() => navigate(t(firstContractPath) + '?action=powChange')}
          />
        </Grid>
      )}

      {Settings?.contract?.ibanChange?.enabled && (
        <Grid item>
          <ActionElement 
            toolTipProps={{
              title: tokenVals?.readonly ?
                t("common:text.token_readonly_tooltip")
              :
                "",
              placement: breakpointDownMd ? "bottom" : "right"
            }}
            title={t("common:text.home_contract_bank_change_title")}
            description={t("common:text.home_contract_bank_change_desc")}
            icon={<AccountBalance />}
            disabled={!!tokenVals?.readonly}
            showTooltip={!!tokenVals?.readonly}
            onClick={() => navigate(t(firstContractPath) + '?action=ibanChange')}
          />
        </Grid>
      )}

      {Settings.features.notificacionAddressChange && (
        <Grid item>
          <ActionElement 
            toolTipProps={{
              title: tokenVals?.readonly ?
                t("common:text.token_readonly_tooltip")
              :
                "",
              placement: breakpointDownMd ? "bottom" : "right"
            }}
            title={t("common:text.home_contract_contact_change_title")}
            description={t("common:text.home_contract_contact_desc")}
            icon={<PermContactCalendar />}
            disabled={!!tokenVals?.readonly}
            showTooltip={!!tokenVals?.readonly}
            onClick={() => navigate(t(firstContractPath) + '?action=notifAddrChange')}
          />
        </Grid>
      )}

      {Settings.features.noCesionSips && (
        <Grid item>
          <ActionElement
            toolTipProps={{
              title: tokenVals?.readonly ?
                t("common:text.token_readonly_tooltip")
              :
                "",
              placement: breakpointDownMd ? "bottom" : "right"
            }}
            title={t("common:text.home_contract_sips_change_title")}
            description={t("common:text.home_contract_sips_change_desc")}
            icon={<Visibility />}
            disabled={!!tokenVals?.readonly}
            showTooltip={!!tokenVals?.readonly}
            onClick={() => navigate(t(firstContractPath) + '?action=noCesionSips')}
          />
        </Grid>
      )}

      <Grid item>
        <Divider variant="middle" sx={{display: {md: "none"}}}/>
      </Grid>

    </Grid>

    <Grid container item direction="column" gap={{xs: 1}} sm={12} md={4}>

      <Grid item>
        <Stack
          direction={{ xs: "row", md: "column" }}
          spacing={2}
          alignItems="center"
        >
          <ActionGroupTitleImage 
            component="img"
            sx={{
              height: {
                xs: "40px",
                sm: "60px",
                md: "80px"
              },
              width: "auto"
            }}
            src={'/images/tabGroup/facturas.png'}
            fallbackComponent={
              <ReceiptOutlined
                color="primary"
                sx={{
                  height: {
                    xs: "40px",
                    sm: "60px",
                    md: "80px"
                  },
                  width: "auto"
                }}
              />
            }
          />
          <Typography variant="h4">
            {t("common:text.home_invoices_title")}
          </Typography>
        </Stack>
      </Grid>

      <Grid item>
        <ActionElement
          title={t("common:text.home_invoices_list_title")}
          description={t("common:text.home_invoices_list_desc")}
          icon={<FindInPage />}
          onClick={() => navigate(t('common:url.invoices'))}
        />
      </Grid>

      <Grid item>
        <ActionElement 
          title={t("common:text.home_invoices_download_title")}
          description={t("common:text.home_invoices_download_desc")}
          icon={<Download />}
          onClick={() => navigate(t('common:url.invoices') + '?action=download')}
        />
      </Grid>

      <Grid item>
        <ActionElement 
          title={t("common:text.home_invoices_online_pay_title")}
          description={t("common:text.home_invoices_online_pay_desc")}
          icon={<Payment />}
          onClick={() => navigate(t('common:url.invoices') + '?action=payOnline')}
        />
      </Grid>

      <Grid item>
        <Divider variant="middle" sx={{display: {md: "none"}}}/>
      </Grid>

    </Grid>

    <Grid container item direction="column" gap={{xs: 1}} sm={12} md={4}>

      <Grid item>
        <Stack
          direction={{ xs: "row", md: "column" }}
          spacing={2}
          alignItems="center"
        >
          <ActionGroupTitleImage 
            component="img"
            sx={{
              height: {
                xs: "40px",
                sm: "60px",
                md: "80px"
              },
              width: "auto"
            }}
            src={'/images/tabGroup/consumo.png'}
            fallbackComponent={
              <TungstenOutlined
                color="primary"
                sx={{
                  height: {
                    xs: "40px",
                    sm: "60px",
                    md: "80px"
                  },
                  width: "auto"
                }}
              />
            }
          />
          <Typography variant="h4">
            {t("common:text.home_consumption_title")}
          </Typography>
        </Stack>
      </Grid>

      <Grid item>
        <ActionElement
          title={t("common:text.home_consumption_view_title")}
          description={t("common:text.home_consumption_view_desc")}
          icon={<BarChart />}
          onClick={() => navigate(t(firstContractPath) + '?action=consumption')}
        />
      </Grid>
      
    </Grid>

  </Grid>
}

export default ActionGroups;
